/* ============================================================
   G Decor - Back-in-Stock widget (Worker-Direct)
   v6 - 20-Aug-2026

   ---- v6: AVAILABILITY IS READ LIVE, NOT FROM A SNAPSHOT ----

   gdProductData is rendered into the page by Liquid ONCE, at page
   render. The theme then keeps its own buy button up to date from
   live data. So on a page that has been open across a stock change
   the two disagree: the button correctly says SOLD OUT while
   gdProductData still says available:true - and the widget, which
   trusted the snapshot, hid the notify button.

   Symptom seen 20-Aug: a page showing "Only 3 left in stock" AND
   "Sold out" at the same time, with no notify button. Two numbers
   for one variant, on one page. It never errored; it just quietly
   withheld the offer.

   => v6 treats the buy button as the live signal. If the customer
      is looking at SOLD OUT, the notify button is offered - whatever
      the render-time snapshot claims. The snapshot is still used,
      but only as one of two reasons to SHOW it, never as a reason
      to hide it.

   ---- v5, kept ----
   NEVER GUESS A VARIANT. v4 auto-picked the first finish, and the
   theme then wrote that variant into the address bar, so it looked
   as though the URL had chosen it. Worse, getCurrentVariant() fell
   back to variants[0] whenever nothing was selected, recording a
   finish the customer never picked, silently. If no finish is
   chosen we now ask for one.

   Titles come from /products/<handle>.js - Shopify's own product
   JSON - rather than a second copy maintained in Liquid.

   Single-variant products are the one exception: with exactly one
   variant there is nothing to choose, so it is selected.
   ============================================================ */

(function () {
  'use strict';

  if (!document.body || document.body.dataset.gdBisInit === '1') return;
  if (!window.gdProductData || !Array.isArray(window.gdProductData.variants)) return;
  document.body.dataset.gdBisInit = '1';

  var ENDPOINT = '/apps/wishlist/bis/subscribe';

  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function findVariantInput() {
    return document.querySelector('input[name="id"][form*="product"], product-form input[name="id"], form[action*="/cart/add"] input[name="id"]');
  }

  function swatchInputs() {
    return $$('variant-picker input[type="radio"], .variant-picker input[type="radio"], [data-variant-picker] input[type="radio"]');
  }

  function pickerEl() {
    return document.querySelector('variant-picker, .variant-picker, [data-variant-picker]');
  }

  function findBuyButton() {
    return (
      document.querySelector('product-form button[name="add"]') ||
      document.querySelector('form[action*="/cart/add"] button[name="add"]') ||
      document.querySelector('[data-add-to-cart]') ||
      document.querySelector('.product-form__submit')
    );
  }

  /* THE LIVE SIGNAL. What the customer is actually looking at. */
  function buyButtonSaysSoldOut() {
    var btn = findBuyButton();
    if (!btn) return false;
    if (btn.disabled) return true;
    var txt = (btn.textContent || '').toLowerCase();
    return txt.indexOf('sold out') > -1 || txt.indexOf('unavailable') > -1;
  }

  function buyButtonState() {
    var btn = findBuyButton();
    if (!btn) return 'none';
    return (btn.disabled ? 'd' : 'e') + '|' + (btn.textContent || '').trim().slice(0, 24);
  }

  function allVariantsSoldOut() {
    var list = window.gdProductData.variants || [];
    return list.length > 0 && list.every(function (v) { return v.available !== true; });
  }

  /* ---- lazy hydration of titles + product id from Shopify ---- */
  var hydrated = false;
  var hydrating = null;

  function hydrate() {
    if (hydrated) return Promise.resolve();
    if (hydrating) return hydrating;
    var pd = window.gdProductData;
    if (!pd || !pd.handle) { hydrated = true; return Promise.resolve(); }

    hydrating = fetch('/products/' + pd.handle + '.js', { headers: { accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j) return;
        if (j.id) pd.id = j.id;
        var byId = {};
        (j.variants || []).forEach(function (v) { byId[String(v.id)] = v; });
        (pd.variants || []).forEach(function (v) {
          var m = byId[String(v.id)];
          if (!m) return;
          if (m.title) v.title = m.title;
          if (typeof m.available === 'boolean') v.available = m.available;
        });
      })
      .catch(function () { /* fail soft - the button signal still works */ })
      .then(function () { hydrated = true; refreshTrigger(); });

    return hydrating;
  }

  /* Returns the variant the customer actually chose, or null.
     NEVER falls back to variants[0] on a multi-variant product. */
  function getCurrentVariant() {
    var list = window.gdProductData.variants || [];
    var input = findVariantInput();

    if (input && input.value) {
      var variantId = String(input.value);
      var found = list.find(function (v) { return String(v.id) === variantId; });
      if (found) return found;
    }

    if (list.length === 1) return list[0];

    return null;
  }

  function getVariantName(v) {
    if (!v) return '';
    if (v.title && v.title !== 'Default Title') return v.title;

    var checked = swatchInputs().filter(function (r) { return r.checked; });
    var values = checked.map(function (r) { return (r.value || '').trim(); }).filter(Boolean);
    if (values.length) return values.join(' / ');

    return '';
  }

  function ensureTrigger() {
    var existing = document.getElementById('gd-bis-trigger');
    if (existing) return existing;
    var anchor = findBuyButton();
    if (!anchor) return null;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'gd-bis-trigger';
    btn.className = 'gd-bis-trigger';
    btn.setAttribute('data-gd-bis-open', '');
    btn.innerHTML = '<span>Notify me when back in stock</span>';
    btn.hidden = true;
    var parent = anchor.parentNode;
    if (anchor.nextSibling) parent.insertBefore(btn, anchor.nextSibling);
    else parent.appendChild(btn);
    return btn;
  }

  /* Shown when the chosen variant is sold out - by EITHER the live
     button or the snapshot - and when nothing is chosen yet on a
     wholly sold-out product, so the offer is visible before the
     customer has picked a finish. */
  function refreshTrigger() {
    var trigger = ensureTrigger();
    if (!trigger) return;
    var v = getCurrentVariant();
    var live = buyButtonSaysSoldOut();

    if (v && (v.available !== true || live)) {
      trigger.hidden = false;
      trigger.dataset.variantId = String(v.id);
      return;
    }
    if (!v && (allVariantsSoldOut() || live)) {
      trigger.hidden = false;
      trigger.removeAttribute('data-variant-id');
      return;
    }
    trigger.hidden = true;
    trigger.removeAttribute('data-variant-id');
  }

  function chooseMsgEl(create) {
    var msg = document.getElementById('gd-bis-choose-msg');
    if (msg || !create) return msg;
    var picker = pickerEl();
    if (!picker) return null;
    msg = document.createElement('p');
    msg.id = 'gd-bis-choose-msg';
    msg.className = 'gd-bis-choose-msg';
    msg.setAttribute('role', 'status');
    msg.hidden = true;
    picker.parentNode.insertBefore(msg, picker.nextSibling);
    return msg;
  }

  function promptForFinish() {
    var picker = pickerEl();
    var msg = chooseMsgEl(true);
    if (msg) {
      msg.textContent = 'Choose a finish first, then we will let you know the moment it lands.';
      msg.hidden = false;
    }
    if (picker) {
      picker.classList.add('gd-bis-needs-choice');
      try { picker.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
    }
  }

  function clearFinishPrompt() {
    var msg = chooseMsgEl(false);
    if (msg) msg.hidden = true;
    var picker = pickerEl();
    if (picker) picker.classList.remove('gd-bis-needs-choice');
  }

  function getModal() { return document.querySelector('[data-gd-bis-modal]'); }

  function openModal() {
    var v = getCurrentVariant();
    if (!v) { promptForFinish(); return; }

    hydrate().then(function () {
      var modal = getModal();
      if (!modal) return;
      var current = getCurrentVariant() || v;
      clearFinishPrompt();

      modal.dataset.variantId = String(current.id);
      var formView = modal.querySelector('[data-gd-bis-view="form"]');
      var successView = modal.querySelector('[data-gd-bis-view="success"]');
      if (formView) formView.hidden = false;
      if (successView) successView.hidden = true;
      var err = modal.querySelector('[data-gd-bis-error]');
      if (err) { err.hidden = true; err.textContent = ''; }
      var emailInput = modal.querySelector('#gd-bis-email');
      if (emailInput) emailInput.removeAttribute('aria-invalid');
      var variantEl = modal.querySelector('[data-gd-bis-variant-name]');
      if (variantEl) variantEl.textContent = getVariantName(current);
      modal.hidden = false;
      requestAnimationFrame(function () { modal.setAttribute('aria-hidden', 'false'); });
      document.body.style.overflow = 'hidden';
      setTimeout(function () {
        var inp = modal.querySelector('#gd-bis-email');
        if (inp) inp.focus();
      }, 60);
    });
  }

  function closeModal() {
    var modal = getModal();
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(function () { modal.hidden = true; }, 220);
  }

  function isValidEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
  }

  function postSubscribe(payload) {
    return fetch(ENDPOINT, {
      method: 'POST',
      mode: 'same-origin',
      credentials: 'same-origin',
      headers: { 'accept': 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  function buildPayload(email, variantId, optInMarketing) {
    var pd = window.gdProductData || {};
    var list = pd.variants || [];
    var v = list.find(function (x) { return String(x.id) === String(variantId); });
    return {
      email: email,
      variant_id: variantId,
      product_id: String(pd.id || '').replace(/\D/g, ''),
      product_handle: pd.handle || '',
      product_title: pd.title || '',
      product_image: pd.image || '',
      product_price: pd.price || '',
      variant_title: getVariantName(v),
      opt_in_marketing: optInMarketing
    };
  }

  function handleSubmit(e) {
    e.preventDefault();
    var modal = getModal();
    if (!modal) return;
    var form = e.currentTarget;
    var emailInput = form.querySelector('#gd-bis-email');
    var optInBox = form.querySelector('input[name="opt_in_marketing"]');
    var errEl = form.querySelector('[data-gd-bis-error]');
    var submitBtn = form.querySelector('[data-gd-bis-submit]');
    var label = form.querySelector('[data-gd-bis-submit-label]');
    var spinner = form.querySelector('[data-gd-bis-spinner]');

    var email = (emailInput.value || '').trim();
    if (!isValidEmail(email)) {
      emailInput.setAttribute('aria-invalid', 'true');
      errEl.textContent = 'Please enter a valid email address.';
      errEl.hidden = false;
      emailInput.focus();
      return;
    }

    var variantId = modal.dataset.variantId;
    if (!variantId) {
      errEl.textContent = 'Please choose a finish, then try again.';
      errEl.hidden = false;
      return;
    }

    submitBtn.disabled = true;
    spinner.hidden = false;
    label.textContent = 'Submitting...';

    postSubscribe(buildPayload(email, variantId, !!(optInBox && optInBox.checked)))
      .then(function (res) {
        return res.json().then(function (body) {
          if (res.ok && body.ok) { showSuccess(); return; }
          throw new Error((body && body.error) || ('Sorry, something went wrong (' + res.status + '). Please try again.'));
        });
      })
      .catch(function (err) {
        errEl.textContent = err && err.message ? err.message : 'Sorry, something went wrong. Please try again.';
        errEl.hidden = false;
      })
      .then(function () {
        submitBtn.disabled = false;
        spinner.hidden = true;
        label.textContent = 'Notify me';
      });
  }

  function showSuccess() {
    var modal = getModal();
    if (!modal) return;
    var formView = modal.querySelector('[data-gd-bis-view="form"]');
    var successView = modal.querySelector('[data-gd-bis-view="success"]');
    var productEcho = modal.querySelector('[data-gd-bis-product-name-echo]');
    if (productEcho && window.gdProductData) {
      productEcho.textContent = window.gdProductData.title || 'this piece';
    }
    if (formView) formView.hidden = true;
    if (successView) successView.hidden = false;
  }

  function attachEvents() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-gd-bis-open], #gd-bis-trigger');
      if (btn) { e.preventDefault(); openModal(); return; }
      var closeBtn = e.target.closest('[data-gd-bis-close]');
      if (closeBtn) { e.preventDefault(); closeModal(); }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var modal = getModal();
        if (modal && modal.getAttribute('aria-hidden') === 'false') closeModal();
      }
    });

    document.addEventListener('change', function (e) {
      if (e.target && e.target.type === 'radio') clearFinishPrompt();
    });

    var form = document.querySelector('[data-gd-bis-form]');
    if (form) form.addEventListener('submit', handleSubmit);

    if (allVariantsSoldOut() || buyButtonSaysSoldOut()) hydrate();

    // Watch BOTH the chosen variant and the live button. The button is
    // what changes when the theme re-renders after a stock change.
    var lastVariantId = null;
    var lastButton = null;
    setInterval(function () {
      var input = findVariantInput();
      var current = input ? input.value : null;
      var btnState = buyButtonState();
      if (current !== lastVariantId || btnState !== lastButton) {
        lastVariantId = current;
        lastButton = btnState;
        refreshTrigger();
      }
    }, 300);

    refreshTrigger();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachEvents);
  } else {
    attachEvents();
  }
})();